import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";

import { auth, db } from "../../firebase";
import { collection, getDocs } from "firebase/firestore";

import SEO from '../SEO';
import Popup from '../Popup';
import Loading from "../Loading";
import MyPageMenu from "./MyPageMenu";
import DeleteSelected from "./DeleteSelected";
import ActTags from './ActTags';
import PushPopup from './PushPopup';

import styleClasses from "./css/MyPage.module.scss";

function MembersList() {
  const navigate = useNavigate();

  const [uid, setUid]                         = useState(null);
  const [members, setMembers]                 = useState([]);
  const [isLoading, setIsLoading]             = useState(true);
  const [selectedMember, setSelectedMember]   = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]); // 선택된 멤버 ID 목록
  
  const [openPopup, setOpenPopup]             = useState(false);
  const [buttonLoading, setButtonLoading]     = useState(false);
  const [popupTitle, setPopupTitle]           = useState('');
  const [popupFunction, setPopupFunction]     = useState('');
  const [popupButtonText, setPopupButtonText] = useState('');

  const [openPushAlbumPopup, setOpenPushAlbumPopup] = useState(false);

  // 🔹 멤버 목록
  const fetchMembers = useCallback(async () => {
    if (!uid) return;

    try {
      const membersRef = collection(db, "users_data", uid, "members");
      const snapshot = await getDocs(membersRef);
      const fetchedMembers = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));
      // 멤버 이름 순으로 정렬 (옵션)
      fetchedMembers.sort((a, b) => a.name.localeCompare(b.name));
      setMembers(fetchedMembers);
    } catch (err) {
      console.error("멤버 데이터를 불러오는 중 오류 발생:", err);
      setOpenPopup(true);
      setPopupTitle(`멤버 데이터를 불러오는 중 오류가 발생했습니다.`);
      setPopupButtonText('');
      setPopupFunction('');
    }
    setIsLoading(false);
  }, [uid]);

  // 🔹 로그인 확인 및 함수 호출
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        navigate('/login');
      } else {
        setUid(user.uid);
        fetchMembers();
      }
    });
    return () => unsubscribe();
  }, [navigate, fetchMembers]);

  // 🔹 선택 체크박스 토글 함수
  const handleCheckboxChange = (memberId) => {
    setSelectedMembers((prevSelected) =>
      prevSelected.includes(memberId)
        ? prevSelected.filter((id) => id !== memberId)
        : [...prevSelected, memberId]
    );
  };

  // 🔹 뮤지카드 적용 팝업 열기
  useEffect(() => {
    if (selectedMember.length) {
      setOpenPushAlbumPopup(true);
    }
  }, [selectedMember]);

  return (
    <>
      <SEO title='멤버 목록' />
      <div className={styleClasses.myPage}>
        <div className={styleClasses.inner}>
          <div className={styleClasses.title}>
            <h1>Members</h1>
            <div className="commonIcon toolTip">
              <i className="questionBlack w20"></i>
              <div className="toolTipText">
                <p>팀 멤버 (팀 뮤지카드용) <br />각 멤버들의 이력을 제일 잘 보여줄 링크와 함께 <br />우리 팀 멤버들을 소개해보세요!</p>
              </div>
            </div>
          </div>
          <div className={styleClasses.editContent}>
            <div className={styleClasses.leftContent}>
              <MyPageMenu currentPage='members' />
            </div>
            <div className={styleClasses.rightContent}>
              <div className={styleClasses.content}>
                <div className={styleClasses.myArchive}>
                  <div className={styleClasses.topButtons}>
                    <div className={`commonIcon toolTip ${ styleClasses.topButtonToolTip }`}>
                      <i className="questionBlack w20"></i>
                      <div className="toolTipText left">
                        <p>팀 멤버 카드를 자유롭게 추가, 수정, 삭제하세요.<br />그리고 원하는 카드만 골라서 뮤지카드에 적용해보세요!</p>
                      </div>
                    </div>
                    <DeleteSelected
                      selectedIDs={selectedMembers}
                      documents={members}
                      collectionPath={`users_data/${uid}/members`}
                      itemName="멤버"
                      subcollections={[]} // 추가 서브컬렉션이 필요하면 여기에 넣으세요.
                      onDeleteSuccess={() => setSelectedMembers([])}
                      fetchData={fetchMembers}
                      setOpenPopup={ setOpenPopup }
                      setButtonLoading={ setButtonLoading }
                      setPopupTitle={ setPopupTitle }
                      setPopupFunction={ setPopupFunction }
                      setPopupButtonText={ setPopupButtonText }
                    />
                    <div className="commonButton fullwidth">
                      <Link to="/my-page/members/new" className="black">
                        추가
                      </Link>
                    </div>
                  </div>
                  <div className={ styleClasses.memberList }>
                    {
                      isLoading
                      ?
                      (
                        <Loading />
                      )
                      :
                      members.length > 0
                      ?
                      (
                        members.map((member) => (
                        <div key={ member.id } className={ styleClasses.eachAlbumCard }>
                          <div className={ styleClasses.checkBox }>
                            <div className='commonForm'>
                              <div className="formRow">
                                <div className="fieldBox checkBox">         
                                  <input type="checkbox" id={`member_${member.id}`} checked={selectedMembers.includes(member.id)} onChange={() => handleCheckboxChange(member.id)} />
                                </div>
                              </div>
                            </div>
                          </div>
                          <Link to={`/my-page/members/${member.id}`} className={ styleClasses.linkBox }>
                            {
                              member.image
                              ?
                              (
                                <img src={member.image} alt={member.name} />
                              )
                              :
                              (
                                <div
                                className={styleClasses.placeholder}
                                style={{ width: "100%", height: "100%" }}
                                >
                                이미지를 지정해주세요.
                                </div>
                              )
                            }
                            <p className={styleClasses.title}>{member.name ? member.name : "이름 없음"}</p>
                            <p className={styleClasses.date}>{member.position ? member.position : "포지션 없음"}</p>
                          </Link>
                          <ActTags
                            act={ member.act }
                            userUid={ uid }
                            attribute="members"
                            albumID={ member.id }
                            fetchData={ fetchMembers }
                            setSelectedItem={ setSelectedMember }  // 여기서 올바른 함수가 전달됩니다.
                            setOpenPopup={ setOpenPopup }
                            setButtonLoading={ setButtonLoading }
                            setPopupTitle={ setPopupTitle }
                            setPopupFunction={ setPopupFunction }
                            setPopupButtonText={ setPopupButtonText }
                          />
                        </div>
                        ))
                      )
                      :
                      (
                        <div className={styleClasses.emptyList}>
                          <h1>등록된 멤버가 없습니다.</h1>
                        </div>
                      )
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* 기존 PushAlbumPopup 대신 제너럴 PushPopup 사용 */}
      <PushPopup
        open={ openPushAlbumPopup }
        setOpenPopup={ setOpenPushAlbumPopup }
        attribute="members"
        selectedItem={ selectedMember }       // 단일 선택
        selectedItems={ [] }                  // 다중 선택
        setSelectedItem={ setSelectedMember } // 단일 선택 초기화
        clearSelectedItems={ () => void(0) }  // 다중 선택 초기화 함수 (필요하면)
        categoryOptions={[]}                  // 필요시 옵션 설정 (없다면 빈 배열)
        defaultCategory=""                    // 필요시 기본 카테고리 설정
        fetchData={ fetchMembers }
        setOpenAlertPopup={ setOpenPopup }
        setPopupTitle={ setPopupTitle }
        setPopupButtonText={ setPopupButtonText }
        setPopupFunction={ setPopupFunction }
      />
      <Popup open={ openPopup } setOpenPopup={ setOpenPopup } title={ popupTitle } text='' buttonText={ popupButtonText } buttonFunction={ popupFunction } buttonLoading={ buttonLoading } />
    </>
  );
}

export default MembersList;